/**
 * Admin panel roles and area permissions.
 *
 * Retro-compatible: missing panelRole + empty permissions ⇒ full access.
 * Org users keep role === 'admin'; panelRole only refines in-app access.
 *
 * Permission keys:
 * - Area (legacy / full): `orders` ⇒ view + write
 * - Action-scoped: `orders:view`, `orders:write`
 *
 * Legacy includes:
 * - `orders` implies `quotations`
 * - `purchasing` implies `accounts_payable`
 */

export const PANEL_PERMISSIONS = {
  ORDERS: 'orders',
  QUOTATIONS: 'quotations',
  INVENTORY: 'inventory',
  ACCOUNTS_RECEIVABLE: 'accounts_receivable',
  PURCHASING: 'purchasing',
  ACCOUNTS_PAYABLE: 'accounts_payable',
  CLIENTS: 'clients',
  VENDORS: 'vendors',
  CONFIGURATION: 'configuration',
  MANAGE_USERS: 'manage_users',
} as const;

export type PanelPermission =
  (typeof PANEL_PERMISSIONS)[keyof typeof PANEL_PERMISSIONS];

export const PANEL_ACTIONS = {
  VIEW: 'view',
  WRITE: 'write',
} as const;

export type PanelAction = (typeof PANEL_ACTIONS)[keyof typeof PANEL_ACTIONS];

export const ALL_PANEL_PERMISSIONS: PanelPermission[] = Object.values(
  PANEL_PERMISSIONS
);

export const ALL_PANEL_ACTIONS: PanelAction[] = Object.values(PANEL_ACTIONS);

export const PANEL_ACTION_LABELS: Record<PanelAction, string> = {
  view: 'Ver',
  write: 'Editar',
};

/** Parent grant still unlocks these children (legacy presets). */
export const PANEL_AREA_INCLUDES: Record<string, readonly string[]> = {
  [PANEL_PERMISSIONS.ORDERS]: [PANEL_PERMISSIONS.QUOTATIONS],
  [PANEL_PERMISSIONS.PURCHASING]: [PANEL_PERMISSIONS.ACCOUNTS_PAYABLE],
};

export function actionPermission(
  area: string,
  action: PanelAction | string
): string {
  return `${area}:${action}`;
}

export function parsePermission(
  permission: string
): { area: string; action: PanelAction | null } {
  const value = String(permission || '').trim();
  if (!value.includes(':')) {
    return { area: value, action: null };
  }
  const [area, action] = value.split(':', 2);
  const normalized = (action || '').trim() as PanelAction;
  return {
    area: (area || '').trim(),
    action: ALL_PANEL_ACTIONS.includes(normalized) ? normalized : null,
  };
}

export function knownPermissionIds(): Set<string> {
  const known = new Set<string>(ALL_PANEL_PERMISSIONS);
  ALL_PANEL_PERMISSIONS.forEach(area => {
    ALL_PANEL_ACTIONS.forEach(action => {
      known.add(actionPermission(area, action));
    });
  });
  return known;
}

const VIEWER_PERMISSIONS: string[] = [
  PANEL_PERMISSIONS.ORDERS,
  PANEL_PERMISSIONS.QUOTATIONS,
  PANEL_PERMISSIONS.INVENTORY,
  PANEL_PERMISSIONS.ACCOUNTS_RECEIVABLE,
  PANEL_PERMISSIONS.PURCHASING,
  PANEL_PERMISSIONS.ACCOUNTS_PAYABLE,
  PANEL_PERMISSIONS.CLIENTS,
  PANEL_PERMISSIONS.VENDORS,
].map(area => actionPermission(area, PANEL_ACTIONS.VIEW));

export const PANEL_ROLES = {
  ADMIN: 'admin',
  OPERATIONS: 'operations',
  COMMERCIAL: 'commercial',
  FINANCE: 'finance',
  VIEWER: 'viewer',
} as const;

export type PanelRole = (typeof PANEL_ROLES)[keyof typeof PANEL_ROLES];

export const PANEL_ROLE_PERMISSIONS: Record<PanelRole, string[]> = {
  admin: [...ALL_PANEL_PERMISSIONS],
  operations: [
    PANEL_PERMISSIONS.ORDERS,
    PANEL_PERMISSIONS.QUOTATIONS,
    PANEL_PERMISSIONS.INVENTORY,
    PANEL_PERMISSIONS.PURCHASING,
    PANEL_PERMISSIONS.ACCOUNTS_PAYABLE,
    PANEL_PERMISSIONS.CLIENTS,
  ],
  commercial: [
    PANEL_PERMISSIONS.ORDERS,
    PANEL_PERMISSIONS.QUOTATIONS,
    PANEL_PERMISSIONS.ACCOUNTS_RECEIVABLE,
    PANEL_PERMISSIONS.CLIENTS,
    PANEL_PERMISSIONS.VENDORS,
  ],
  finance: [
    PANEL_PERMISSIONS.ORDERS,
    PANEL_PERMISSIONS.ACCOUNTS_RECEIVABLE,
    PANEL_PERMISSIONS.PURCHASING,
    PANEL_PERMISSIONS.ACCOUNTS_PAYABLE,
  ],
  viewer: [...VIEWER_PERMISSIONS],
};

export const PANEL_ROLE_LABELS: Record<PanelRole, string> = {
  admin: 'Administrador',
  operations: 'Operaciones',
  commercial: 'Comercial',
  finance: 'Finanzas',
  viewer: 'Solo lectura',
};

export const PANEL_ROLE_DESCRIPTIONS: Record<PanelRole, string> = {
  admin: 'Acceso completo a todas las áreas del panel',
  operations: 'Inventario, compras, órdenes, cotizaciones y clientes',
  commercial:
    'Órdenes, cotizaciones, clientes, vendedores y cuentas por cobrar',
  finance: 'Órdenes, cuentas por cobrar/pagar y compras',
  viewer: 'Solo consulta en áreas operativas (sin editar ni configurar)',
};

export const PANEL_PERMISSION_CATALOG: Array<{
  id: PanelPermission;
  label: string;
  description: string;
  group: string;
  actions: PanelAction[];
}> = [
  {
    id: PANEL_PERMISSIONS.ORDERS,
    label: 'Órdenes / Ventas',
    description: 'Ventas y devoluciones',
    group: 'Operación',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.QUOTATIONS,
    label: 'Cotizaciones',
    description:
      'Cotizaciones (también cubierto por Órdenes por compatibilidad)',
    group: 'Operación',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.INVENTORY,
    label: 'Inventario',
    description: 'Catálogo de productos y stock',
    group: 'Operación',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.ACCOUNTS_RECEIVABLE,
    label: 'Cuentas por cobrar',
    description: 'Cobranza y estado de cuentas de clientes',
    group: 'Finanzas',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.PURCHASING,
    label: 'Compras',
    description: 'Proveedores y compras',
    group: 'Finanzas',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.ACCOUNTS_PAYABLE,
    label: 'Cuentas por pagar',
    description:
      'Cuentas por pagar (también cubierto por Compras por compatibilidad)',
    group: 'Finanzas',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.CLIENTS,
    label: 'Clientes',
    description: 'Gestión de clientes',
    group: 'Comercial',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.VENDORS,
    label: 'Vendedores',
    description: 'Gestión de vendedores y comisiones',
    group: 'Comercial',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.CONFIGURATION,
    label: 'Configuración',
    description:
      'Ajustes de la organización, integraciones, zonas y almacenes',
    group: 'Administración',
    actions: [...ALL_PANEL_ACTIONS],
  },
  {
    id: PANEL_PERMISSIONS.MANAGE_USERS,
    label: 'Gestión de usuarios',
    description: 'Crear usuarios del panel y definir roles',
    group: 'Administración',
    actions: [...ALL_PANEL_ACTIONS],
  },
];

/** Path prefix → required permission (area access) */
export const ROUTE_PERMISSION_MAP: Array<{
  prefix: string;
  permission: PanelPermission;
}> = [
  { prefix: '/orders', permission: PANEL_PERMISSIONS.ORDERS },
  { prefix: '/quotations', permission: PANEL_PERMISSIONS.QUOTATIONS },
  { prefix: '/client-orders', permission: PANEL_PERMISSIONS.ORDERS },
  { prefix: '/returns', permission: PANEL_PERMISSIONS.ORDERS },
  { prefix: '/inventory', permission: PANEL_PERMISSIONS.INVENTORY },
  {
    prefix: '/accounts-receivable',
    permission: PANEL_PERMISSIONS.ACCOUNTS_RECEIVABLE,
  },
  { prefix: '/suppliers', permission: PANEL_PERMISSIONS.PURCHASING },
  { prefix: '/purchases', permission: PANEL_PERMISSIONS.PURCHASING },
  {
    prefix: '/accounts-payable',
    permission: PANEL_PERMISSIONS.ACCOUNTS_PAYABLE,
  },
  { prefix: '/clients', permission: PANEL_PERMISSIONS.CLIENTS },
  { prefix: '/vendors', permission: PANEL_PERMISSIONS.VENDORS },
  { prefix: '/configuration', permission: PANEL_PERMISSIONS.CONFIGURATION },
];

export function isPanelRole(value?: string | null): value is PanelRole {
  return !!value && value in PANEL_ROLE_PERMISSIONS;
}

export function compactPermissionIds(raw?: string[] | null): string[] {
  const known = knownPermissionIds();
  const validated = Array.from(
    new Set((raw || []).map(v => String(v || '').trim()).filter(Boolean))
  ).filter(v => known.has(v));

  const bare = new Set<string>();
  const byArea = new Map<string, Set<string>>();
  validated.forEach(perm => {
    const { area, action } = parsePermission(perm);
    if (!action) {
      bare.add(area);
      return;
    }
    if (!byArea.has(area)) byArea.set(area, new Set());
    byArea.get(area)!.add(action);
  });

  const out: string[] = [];
  const seen = new Set<string>();
  ALL_PANEL_PERMISSIONS.forEach(area => {
    if (bare.has(area)) {
      out.push(area);
      seen.add(area);
      seen.add(actionPermission(area, PANEL_ACTIONS.VIEW));
      seen.add(actionPermission(area, PANEL_ACTIONS.WRITE));
      return;
    }
    const actions = byArea.get(area) || new Set();
    if (actions.has(PANEL_ACTIONS.VIEW) && actions.has(PANEL_ACTIONS.WRITE)) {
      out.push(area);
      seen.add(area);
      seen.add(actionPermission(area, PANEL_ACTIONS.VIEW));
      seen.add(actionPermission(area, PANEL_ACTIONS.WRITE));
      return;
    }
    ALL_PANEL_ACTIONS.forEach(action => {
      if (actions.has(action)) {
        const key = actionPermission(area, action);
        out.push(key);
        seen.add(key);
      }
    });
  });
  validated.forEach(perm => {
    if (!seen.has(perm)) out.push(perm);
  });
  return out;
}

export type OrgPanelAccess = {
  panelRole?: string | null;
  permissions?: string[] | null;
};

export function extractOrgPanelAccess(
  userData: {
    panelRole?: string | null;
    permissions?: string[] | null;
    panelAccessByOrg?: Record<string, OrgPanelAccess> | null;
  } | null
    | undefined,
  organizationId?: string | null
): { panelRole?: string | null; permissions?: string[] | null } {
  if (!userData) return {};
  const byOrg = userData.panelAccessByOrg || {};
  if (organizationId && byOrg[organizationId]) {
    const entry = byOrg[organizationId];
    if (entry?.panelRole != null || entry?.permissions != null) {
      return {
        panelRole: entry.panelRole,
        permissions: entry.permissions,
      };
    }
  }
  return {
    panelRole: userData.panelRole,
    permissions: userData.permissions,
  };
}

export function resolveEffectivePermissions(options: {
  panelRole?: string | null;
  permissions?: string[] | null;
  isGodMode?: boolean;
}): string[] {
  if (options.isGodMode) {
    return [...ALL_PANEL_PERMISSIONS];
  }

  const explicit = (options.permissions || []).filter(Boolean) as string[];
  if (explicit.length > 0) {
    return Array.from(new Set(explicit));
  }

  if (options.panelRole && isPanelRole(options.panelRole)) {
    return [...PANEL_ROLE_PERMISSIONS[options.panelRole]];
  }

  // Custom / unknown role without materialized permissions ⇒ no access until resolved
  if (options.panelRole) {
    return [];
  }

  // Legacy admin users: no panelRole / empty permissions ⇒ full access
  return [...ALL_PANEL_PERMISSIONS];
}

function expandImpliedAreas(area: string): Set<string> {
  const matched = new Set<string>([area]);
  Object.entries(PANEL_AREA_INCLUDES).forEach(([parent, children]) => {
    if (children.includes(area)) matched.add(parent);
  });
  return matched;
}

export function hasPanelPermission(
  effective: string[] | null | undefined,
  permission: string,
  isGodMode = false
): boolean {
  if (isGodMode) return true;
  const required = String(permission || '').trim();
  if (!required) return false;

  const perms = new Set(
    (effective || []).map(p => String(p).trim()).filter(Boolean)
  );
  if (perms.has(required)) return true;

  const { area, action } = parsePermission(required);
  if (!area) return false;

  for (const candidate of Array.from(expandImpliedAreas(area))) {
    if (perms.has(candidate)) return true;

    if (!action || action === PANEL_ACTIONS.VIEW) {
      if (
        perms.has(actionPermission(candidate, PANEL_ACTIONS.VIEW)) ||
        perms.has(actionPermission(candidate, PANEL_ACTIONS.WRITE))
      ) {
        return true;
      }
    } else if (action === PANEL_ACTIONS.WRITE) {
      if (perms.has(actionPermission(candidate, PANEL_ACTIONS.WRITE))) {
        return true;
      }
    }
  }

  return false;
}

export function canViewPanelArea(
  effective: string[] | null | undefined,
  area: string,
  isGodMode = false
): boolean {
  return hasPanelPermission(
    effective,
    actionPermission(area, PANEL_ACTIONS.VIEW),
    isGodMode
  );
}

export function canWritePanelArea(
  effective: string[] | null | undefined,
  area: string,
  isGodMode = false
): boolean {
  return hasPanelPermission(
    effective,
    actionPermission(area, PANEL_ACTIONS.WRITE),
    isGodMode
  );
}

export function permissionForPath(
  pathname: string
): PanelPermission | null {
  const match = ROUTE_PERMISSION_MAP.find(entry =>
    pathname.startsWith(entry.prefix)
  );
  return match?.permission ?? null;
}

export function canAccessConfigurationPath(
  effective: string[] | null | undefined,
  isGodMode = false
): boolean {
  return (
    canViewPanelArea(effective, PANEL_PERMISSIONS.CONFIGURATION, isGodMode) ||
    canViewPanelArea(effective, PANEL_PERMISSIONS.MANAGE_USERS, isGodMode)
  );
}

export function canAccessPath(
  pathname: string,
  effective: string[] | null | undefined,
  isGodMode = false
): boolean {
  if (isGodMode) return true;
  if (pathname.startsWith('/configuration')) {
    // null ⇒ still loading; allow briefly (ProtectedRoute shows loader)
    if (effective == null) return true;
    return canAccessConfigurationPath(effective, isGodMode);
  }
  const required = permissionForPath(pathname);
  if (!required) return true;
  // null/undefined ⇒ permissions not loaded yet; allow and let ProtectedRoute
  // show a loader instead of bouncing the user.
  if (effective == null) return true;
  return hasPanelPermission(effective, required, isGodMode);
}

export function defaultLandingPath(
  effective: string[] | null | undefined,
  isGodMode = false
): string {
  const candidates = [
    '/home',
    '/orders',
    '/quotations',
    '/inventory',
    '/accounts-receivable',
    '/clients',
    '/vendors',
    '/suppliers',
    '/accounts-payable',
    '/configuration',
  ];
  for (const path of candidates) {
    if (canAccessPath(path, effective, isGodMode)) {
      return path;
    }
  }
  return '/home';
}

/** Human labels for stored permission ids (area or area:action). */
export function permissionDisplayLabel(
  permissionId: string,
  catalog = PANEL_PERMISSION_CATALOG
): string {
  const { area, action } = parsePermission(permissionId);
  const areaLabel =
    catalog.find(c => c.id === area)?.label || area || permissionId;
  if (!action) return areaLabel;
  const actionLabel =
    PANEL_ACTION_LABELS[action as PanelAction] || action;
  return `${areaLabel} (${actionLabel})`;
}
